import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, Plus, Trash2, CalendarClock, Eye, X, Send, Sparkles } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';

interface ReportTemplate { id: number; name: string; }

interface GeneratedReport {
  id: number;
  template_name: string | null;
  tenant_contact_email: string | null;
  period_start: string;
  period_end: string;
  pdf_size_bytes: number;
  generated_at: string;
  delivered_to: string | null;
  // Considerazioni AI (2026-10-20): presenti, riviste (modificate o confermate), modificate.
  has_ai: boolean;
  ai_reviewed_at: string | null;
  ai_edited_at: string | null;
}

interface AiRisk { title: string; detail: string; priority: 'high' | 'medium' | 'low' }
interface AiAction { title: string; detail: string; horizon: 'immediate' | 'short_term' | 'medium_term' }
interface AiInsights {
  overview: string;
  posture: 'good' | 'fair' | 'weak';
  strengths: string[];
  risks: AiRisk[];
  actions: AiAction[];
  closing: string;
  model?: string;
  generatedAt?: string;
}

type PendingAction = 'download' | 'send';

interface Schedule {
  id: number;
  template_id: number;
  template_name: string;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annual';
  period_days: number;
  enabled: boolean;
  next_run_at: string;
  last_run_at: string | null;
}

function formatBytes(bytes: number): string {
  return bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function ReportsPage() {
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showSchedules, setShowSchedules] = useState(false);

  const [templateId, setTemplateId] = useState<number | ''>('');
  const [periodStart, setPeriodStart] = useState(isoDate(new Date(Date.now() - 30 * 86400000)));
  const [periodEnd, setPeriodEnd] = useState(isoDate(new Date()));
  const [emailCopy, setEmailCopy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Anteprima del PDF in una finestra sovrapposta (blob scaricato via axios, come il download).
  const [preview, setPreview] = useState<{ report: GeneratedReport; url: string } | null>(null);
  // Considerazioni AI: finestra "vuoi modificarle prima di scaricare/inviare?" e editor.
  const [aiAsk, setAiAsk] = useState<{ report: GeneratedReport; action: PendingAction } | null>(null);
  const [aiEditor, setAiEditor] = useState<{ report: GeneratedReport; insights: AiInsights; after: PendingAction | null } | null>(null);
  const [aiSaving, setAiSaving] = useState(false);
  const [sending, setSending] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [scheduleFrequency, setScheduleFrequency] = useState<Schedule['frequency']>('monthly');
  const [schedulePeriodDays, setSchedulePeriodDays] = useState(30);

  async function load() {
    if (!selectedTenantId) return;
    const tenantRes = await axios.get(`/api/tenants/${selectedTenantId}`);
    const partnerId = tenantRes.data.data.partner_id;
    const [templatesRes, reportsRes, schedulesRes] = await Promise.all([
      axios.get('/api/report-templates', { params: { partnerId } }),
      axios.get('/api/reports', { params: { tenantId: selectedTenantId } }),
      axios.get('/api/report-schedules', { params: { tenantId: selectedTenantId } }),
    ]);
    setTemplates(templatesRes.data.data);
    setReports(reportsRes.data.data);
    setSchedules(schedulesRes.data.data);
    if (!templateId && templatesRes.data.data[0]) setTemplateId(templatesRes.data.data[0].id);
  }

  useEffect(() => { load(); }, [selectedTenantId]);

  async function generate() {
    if (!selectedTenantId || !templateId) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post('/api/reports/generate', {
        tenantId: selectedTenantId, templateId,
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd + 'T23:59:59').toISOString(),
        sendEmailCopy: emailCopy,
      });
      await load();
      // Con considerazioni AI l'email non è partita: si chiede prima se modificarle.
      if (res.data.data?.pendingEmail) {
        const created: GeneratedReport = { ...res.data.data, tenant_contact_email: res.data.data.tenant_contact_email ?? null };
        setAiAsk({ report: created, action: 'send' });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  }

  async function createSchedule() {
    if (!selectedTenantId || !templateId) return;
    await axios.post('/api/report-schedules', {
      tenantId: selectedTenantId, templateId, frequency: scheduleFrequency, periodDays: schedulePeriodDays,
    });
    load();
  }

  async function toggleSchedule(s: Schedule) {
    await axios.put(`/api/report-schedules/${s.id}`, { enabled: !s.enabled });
    load();
  }

  async function deleteSchedule(s: Schedule) {
    if (!window.confirm(`Delete this ${s.frequency} schedule?`)) return;
    await axios.delete(`/api/report-schedules/${s.id}`);
    load();
  }

  // window.open(url) non porta con sé l'header Authorization (axios lo imposta solo sulle proprie
  // richieste): un GET diretto del browser su un endpoint autenticato fallisce con "Access token
  // required". Serve scaricare via axios (che lo include) e poi salvare il blob risultante.
  async function fetchPdfUrl(r: GeneratedReport): Promise<string> {
    const res = await axios.get(`/api/reports/${r.id}/download`, { responseType: 'blob' });
    return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  }

  async function download(r: GeneratedReport) {
    const url = await fetchPdfUrl(r);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${r.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function openPreview(r: GeneratedReport) {
    const url = await fetchPdfUrl(r);
    setPreview({ report: r, url });
  }

  function closePreview() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function sendReport(r: GeneratedReport) {
    setSending(r.id);
    setError(null);
    try {
      const res = await axios.post(`/api/reports/${r.id}/send`);
      setNotice(`Report sent to ${res.data.data.deliveredTo}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not send the report');
    } finally {
      setSending(null);
    }
  }

  function runAction(r: GeneratedReport, action: PendingAction) {
    if (action === 'download') download(r);
    else sendReport(r);
  }

  // Download e invio: se il report ha considerazioni AI non ancora riviste, prima si chiede.
  function withAiCheck(r: GeneratedReport, action: PendingAction) {
    if (r.has_ai && !r.ai_reviewed_at) setAiAsk({ report: r, action });
    else runAction(r, action);
  }

  async function openAiEditor(r: GeneratedReport, after: PendingAction | null) {
    setAiAsk(null);
    try {
      const res = await axios.get(`/api/reports/${r.id}/ai`);
      if (!res.data.data.insights) { setError('This report has no AI considerations to edit'); return; }
      setAiEditor({ report: r, insights: res.data.data.insights, after });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not load the AI considerations');
    }
  }

  async function keepAiAsIs() {
    if (!aiAsk) return;
    const { report, action } = aiAsk;
    setAiAsk(null);
    try {
      await axios.post(`/api/reports/${report.id}/ai/confirm`);
    } catch { /* non blocca l'azione */ }
    runAction({ ...report, ai_reviewed_at: new Date().toISOString() }, action);
    load();
  }

  async function saveAiEditor() {
    if (!aiEditor) return;
    setAiSaving(true);
    setError(null);
    try {
      await axios.put(`/api/reports/${aiEditor.report.id}/ai`, { insights: aiEditor.insights });
      const { report, after } = aiEditor;
      setAiEditor(null);
      if (preview?.report.id === report.id) closePreview();
      await load();
      if (after) runAction({ ...report, ai_reviewed_at: new Date().toISOString() }, after);
      else setNotice('AI considerations saved and the PDF regenerated');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not save the AI considerations');
    } finally {
      setAiSaving(false);
    }
  }

  function editInsights(update: (i: AiInsights) => AiInsights) {
    setAiEditor(prev => prev ? { ...prev, insights: update(prev.insights) } : prev);
  }

  async function deleteReport(r: GeneratedReport) {
    const period = `${new Date(r.period_start).toLocaleDateString('en-GB')} – ${new Date(r.period_end).toLocaleDateString('en-GB')}`;
    if (!window.confirm(`Delete the report "${r.template_name || 'report'}" for ${period}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/reports/${r.id}`);
      if (preview?.report.id === r.id) closePreview();
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not delete the report');
    }
  }

  if (!selectedTenantId) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-500 text-sm">Select a specific tenant from the "Viewing" menu in the sidebar to manage its reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2">
          <FileText size={20} /> Reports — {selectedTenantName}
        </h1>
        <button onClick={() => setShowSchedules(v => !v)} className="btn-secondary flex items-center gap-1.5">
          <CalendarClock size={14} /> Schedules
        </button>
      </div>

      <div className="card p-4">
        <h2 className="font-medium text-sm text-gray-900 mb-3">Generate a report</h2>
        {error && <div className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
        {notice && <div className="mb-3 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2 flex items-center justify-between">{notice}<button onClick={() => setNotice(null)} className="text-green-700"><X size={14} /></button></div>}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Template</label>
            <select value={templateId} onChange={e => setTemplateId(Number(e.target.value))} className="input-field">
              <option value="">Select…</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="input-field" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 mb-2">
            <input type="checkbox" checked={emailCopy} onChange={e => setEmailCopy(e.target.checked)} />
            Email a copy to the tenant contact
          </label>
          <button onClick={generate} disabled={!templateId || generating} className="btn-primary disabled:opacity-50">
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {templates.length === 0 && (
          <p className="text-xs text-amber-600 mt-3">No report template available for this tenant's partner — create one first in Report Templates.</p>
        )}
      </div>

      {showSchedules && (
        <div className="card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100">
            <h2 className="font-medium text-sm text-gray-900">Recurring schedules</h2>
          </div>
          <table className="w-full text-sm">
            <thead><tr>
              <th className="table-header">Template</th><th className="table-header">Frequency</th>
              <th className="table-header">Period (days)</th><th className="table-header">Next run</th>
              <th className="table-header">Status</th><th className="table-header"></th>
            </tr></thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} className="border-t border-gray-100">
                  <td className="table-cell">{s.template_name}</td>
                  <td className="table-cell capitalize text-gray-500">{s.frequency}</td>
                  <td className="table-cell text-gray-500">{s.period_days}</td>
                  <td className="table-cell text-gray-500">{new Date(s.next_run_at).toLocaleDateString('en-GB')}</td>
                  <td className="table-cell">
                    <button onClick={() => toggleSchedule(s)} className={`badge ${s.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {s.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="table-cell">
                    <button onClick={() => deleteSchedule(s)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              <tr className="border-t border-gray-100 bg-gray-50/50">
                <td className="table-cell text-gray-400 text-xs">Uses the template selected above</td>
                <td className="table-cell">
                  <select value={scheduleFrequency} onChange={e => setScheduleFrequency(e.target.value as Schedule['frequency'])} className="input-field py-0.5 px-1.5 text-xs">
                    <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option><option value="annual">Annual</option>
                  </select>
                </td>
                <td className="table-cell">
                  <input type="number" value={schedulePeriodDays} onChange={e => setSchedulePeriodDays(Number(e.target.value))}
                    className="input-field py-0.5 px-1.5 text-xs w-20" />
                </td>
                <td className="table-cell" colSpan={2}></td>
                <td className="table-cell">
                  <button onClick={createSchedule} disabled={!templateId}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40">
                    <Plus size={12} /> Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h2 className="font-medium text-sm text-gray-900">Generated reports</h2>
        </div>
        <table className="w-full text-sm">
          <thead><tr>
            <th className="table-header">Template</th><th className="table-header">Period</th>
            <th className="table-header">Size</th><th className="table-header">Generated</th>
            <th className="table-header">AI</th>
            <th className="table-header">Delivered to</th><th className="table-header"></th>
          </tr></thead>
          <tbody>
            {reports.map(r => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="table-cell">{r.template_name || '—'}</td>
                <td className="table-cell text-gray-500">
                  {new Date(r.period_start).toLocaleDateString('en-GB')} – {new Date(r.period_end).toLocaleDateString('en-GB')}
                </td>
                <td className="table-cell text-gray-500">{formatBytes(r.pdf_size_bytes)}</td>
                <td className="table-cell text-gray-500">{new Date(r.generated_at).toLocaleString('en-GB')}</td>
                <td className="table-cell">
                  {!r.has_ai ? <span className="text-gray-300">—</span> : (
                    <button onClick={() => openAiEditor(r, null)} title="Edit the AI considerations"
                      className={`badge inline-flex items-center gap-1 ${r.ai_edited_at ? 'bg-primary-50 text-primary-700' : r.ai_reviewed_at ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      <Sparkles size={11} /> {r.ai_edited_at ? 'Edited' : r.ai_reviewed_at ? 'Reviewed' : 'To review'}
                    </button>
                  )}
                </td>
                <td className="table-cell text-gray-500">{r.delivered_to || '—'}</td>
                <td className="table-cell">
                  <span className="inline-flex items-center gap-3">
                    <button onClick={() => openPreview(r)} className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs" title="Preview">
                      <Eye size={13} /> Preview
                    </button>
                    <button onClick={() => withAiCheck(r, 'download')} className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs" title="Download">
                      <Download size={13} /> Download
                    </button>
                    <button onClick={() => withAiCheck(r, 'send')} disabled={sending === r.id || !r.tenant_contact_email}
                      className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs disabled:opacity-40 disabled:no-underline"
                      title={r.tenant_contact_email ? `Email to ${r.tenant_contact_email}` : 'The tenant has no contact email'}>
                      <Send size={13} /> {sending === r.id ? 'Sending…' : 'Send'}
                    </button>
                    <button onClick={() => deleteReport(r)} className="inline-flex items-center gap-1 text-gray-400 hover:text-red-600 text-xs" title="Delete">
                      <Trash2 size={13} /> Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
            {reports.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={7}>No report generated yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
              <div className="text-sm">
                <span className="font-medium text-gray-900">{preview.report.template_name || 'Report'}</span>
                <span className="text-gray-500 ml-2">
                  {new Date(preview.report.period_start).toLocaleDateString('en-GB')} – {new Date(preview.report.period_end).toLocaleDateString('en-GB')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {preview.report.has_ai && (
                  <button onClick={() => openAiEditor(preview.report, null)} className="btn-secondary flex items-center gap-1 text-xs"><Sparkles size={13} /> Edit AI considerations</button>
                )}
                <button onClick={() => withAiCheck(preview.report, 'download')} className="btn-secondary flex items-center gap-1 text-xs"><Download size={13} /> Download</button>
                <button onClick={() => deleteReport(preview.report)} className="btn-secondary flex items-center gap-1 text-xs text-red-600"><Trash2 size={13} /> Delete</button>
                <button onClick={closePreview} className="p-1.5 text-gray-400 hover:text-gray-700 rounded" title="Close"><X size={16} /></button>
              </div>
            </div>
            <iframe src={preview.url} title="Report preview" className="flex-1 w-full rounded-b-xl" />
          </div>
        </div>
      )}

      {aiAsk && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="font-medium text-gray-900 inline-flex items-center gap-2"><Sparkles size={16} className="text-primary-600" /> AI considerations not reviewed yet</h3>
            <p className="text-sm text-gray-600 mt-2">
              This report contains considerations written by the AI model that nobody has reviewed. Do you want to
              edit them before {aiAsk.action === 'send' ? 'sending the report' : 'downloading it'}?
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setAiAsk(null)} className="btn-secondary">Cancel</button>
              <button onClick={keepAiAsIs} className="btn-secondary">Keep as they are and {aiAsk.action === 'send' ? 'send' : 'download'}</button>
              <button onClick={() => openAiEditor(aiAsk.report, aiAsk.action)} className="btn-primary">Edit them</button>
            </div>
          </div>
        </div>
      )}

      {aiEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 inline-flex items-center gap-2"><Sparkles size={16} className="text-primary-600" /> Edit the AI considerations</h3>
              <button onClick={() => setAiEditor(null)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <label className="text-xs text-gray-500">Overall posture</label>
                <select value={aiEditor.insights.posture} onChange={e => editInsights(i => ({ ...i, posture: e.target.value as AiInsights['posture'] }))} className="input-field">
                  <option value="good">Good</option><option value="fair">Fair</option><option value="weak">Weak</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Overview</label>
                <textarea value={aiEditor.insights.overview} rows={5} onChange={e => editInsights(i => ({ ...i, overview: e.target.value }))} className="input-field w-full" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500">What is working</label>
                  <button onClick={() => editInsights(i => ({ ...i, strengths: [...i.strengths, ''] }))} className="text-xs text-primary-600 hover:underline">+ Add</button>
                </div>
                {aiEditor.insights.strengths.map((s, idx) => (
                  <div key={idx} className="flex gap-2 mb-1">
                    <input value={s} onChange={e => editInsights(i => ({ ...i, strengths: i.strengths.map((x, k) => k === idx ? e.target.value : x) }))} className="input-field w-full" />
                    <button onClick={() => editInsights(i => ({ ...i, strengths: i.strengths.filter((_, k) => k !== idx) }))} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500">Main risks</label>
                  <button onClick={() => editInsights(i => ({ ...i, risks: [...i.risks, { title: '', detail: '', priority: 'medium' }] }))} className="text-xs text-primary-600 hover:underline">+ Add</button>
                </div>
                {aiEditor.insights.risks.map((r, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-lg p-2 mb-2">
                    <div className="flex gap-2 mb-1">
                      <input value={r.title} placeholder="Title" onChange={e => editInsights(i => ({ ...i, risks: i.risks.map((x, k) => k === idx ? { ...x, title: e.target.value } : x) }))} className="input-field w-full" />
                      <select value={r.priority} onChange={e => editInsights(i => ({ ...i, risks: i.risks.map((x, k) => k === idx ? { ...x, priority: e.target.value as AiRisk['priority'] } : x) }))} className="input-field">
                        <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                      </select>
                      <button onClick={() => editInsights(i => ({ ...i, risks: i.risks.filter((_, k) => k !== idx) }))} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                    <textarea value={r.detail} rows={2} placeholder="Detail" onChange={e => editInsights(i => ({ ...i, risks: i.risks.map((x, k) => k === idx ? { ...x, detail: e.target.value } : x) }))} className="input-field w-full" />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500">Recommended actions</label>
                  <button onClick={() => editInsights(i => ({ ...i, actions: [...i.actions, { title: '', detail: '', horizon: 'short_term' }] }))} className="text-xs text-primary-600 hover:underline">+ Add</button>
                </div>
                {aiEditor.insights.actions.map((a, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-lg p-2 mb-2">
                    <div className="flex gap-2 mb-1">
                      <input value={a.title} placeholder="Title" onChange={e => editInsights(i => ({ ...i, actions: i.actions.map((x, k) => k === idx ? { ...x, title: e.target.value } : x) }))} className="input-field w-full" />
                      <select value={a.horizon} onChange={e => editInsights(i => ({ ...i, actions: i.actions.map((x, k) => k === idx ? { ...x, horizon: e.target.value as AiAction['horizon'] } : x) }))} className="input-field">
                        <option value="immediate">Immediate</option><option value="short_term">Short term</option><option value="medium_term">Medium term</option>
                      </select>
                      <button onClick={() => editInsights(i => ({ ...i, actions: i.actions.filter((_, k) => k !== idx) }))} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                    <textarea value={a.detail} rows={2} placeholder="Detail" onChange={e => editInsights(i => ({ ...i, actions: i.actions.map((x, k) => k === idx ? { ...x, detail: e.target.value } : x) }))} className="input-field w-full" />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Closing sentence</label>
                <textarea value={aiEditor.insights.closing} rows={2} onChange={e => editInsights(i => ({ ...i, closing: e.target.value }))} className="input-field w-full" />
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Saving regenerates the PDF; links, emails and phone numbers are removed from the text.</span>
              <div className="flex gap-2">
                <button onClick={() => setAiEditor(null)} className="btn-secondary">Cancel</button>
                <button onClick={saveAiEditor} disabled={aiSaving} className="btn-primary disabled:opacity-50">
                  {aiSaving ? 'Saving…' : aiEditor.after === 'send' ? 'Save and send' : aiEditor.after === 'download' ? 'Save and download' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
